import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { TranslateModule } from '@ngx-translate/core';

export type AutoCountdownDialogResult = 'keepActive' | 'end' | 'expired';

const COUNTDOWN_MS = 60_000;
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

@Component({
  selector: 'lib-auto-countdown-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './auto-countdown-dialog.component.html',
  styleUrl: './auto-countdown-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutoCountdownDialogComponent implements OnInit {
  private readonly dialogRef = inject<DialogRef<AutoCountdownDialogResult>>(DialogRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly remainingMs = signal(COUNTDOWN_MS);

  protected readonly remainingLabel = computed(() => {
    const totalSeconds = Math.max(0, Math.ceil(this.remainingMs() / MS_PER_SECOND));
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  private intervalHandle: ReturnType<typeof setInterval> | undefined;

  public ngOnInit(): void {
    this.intervalHandle = setInterval(() => {
      const next = this.remainingMs() - MS_PER_SECOND;
      this.remainingMs.set(next > 0 ? next : 0);

      if (next <= 0) {
        this.clearInterval();
        this.dialogRef.close('expired');
      }
    }, MS_PER_SECOND);

    this.destroyRef.onDestroy(() => this.clearInterval());
  }

  protected onKeepActive(): void {
    this.dialogRef.close('keepActive');
  }

  protected onEndAppointment(): void {
    this.dialogRef.close('end');
  }

  private clearInterval(): void {
    if (this.intervalHandle !== undefined) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = undefined;
    }
  }
}
